import SoftPhoneModule from '@modules/SoftPhoneModule';
import { useCallDuration } from './hooks/useCallDuration';
import { useEffect } from 'react';
import Draggable from '@components/DragAndDrop/components/Draggable/Draggable';
import { DraggableItemIds } from '@components/DragAndDrop/components/draggableItemIds';
import getDisplayName from '@lib/utils/getDisplayName';
import useService from '@modules/ServiceModule/useService';
import { useWindowSize } from 'usehooks-ts';
import CallControls from './components/CallControls';
import useRecording from './hooks/useRecording';
import TasksModule from '@modules/TasksModule';
import { CallState } from '@modules/SoftPhoneModule/types';

const CALL_CONTROLS_WIDTH_PX = 777;
const CALL_CONTROLS_BOTTOM_OFFSET = 100;

interface CallBarProps {
  callState: CallState;
  show: boolean;
}

export function CallBar({ callState, show }: CallBarProps) {
  const {
    direction,
    established,
    hasSession,
    held,
    inTransfer,
    isAudioMuted,
    isVideoMuted,
    progress,
    remoteIdentity,
    internalCall,
    terminating,
    remoteCallId,
    pcsConversationSid,
  } = callState;
  const { users } = useService();

  const isInCall = direction === 'terminating' ? established : hasSession;
  const task = TasksModule.findTaskByPcsConversationSid(pcsConversationSid);
  const callId = task?.attributes.callId;

  const identity = getDisplayName(
    task?.displayName ?? remoteIdentity,
    internalCall,
    users,
  );

  const { stop, start, formattedTime } = useCallDuration();

  const handleHangup = () => {
    stop();
    SoftPhoneModule.callControl.hangup(callId);
  };

  const handleAnswer = () => {
    stop();
    SoftPhoneModule.callControl.answer(callId);
  };

  const {
    isRecording,
    isRecordingDisabled,
    isRecordingLoading,
    recordingTooltip,
    toggleRecording,
  } = useRecording(callId);

  // This useEffect stops the timer when the call ends
  useEffect(() => {
    if (!isInCall) {
      stop();
    }
  }, [isInCall, stop]);

  const { width: windowWidth } = useWindowSize();

  const handleCalculateAbsolutePosition = (x: number, y: number) => ({
    bottom: `${CALL_CONTROLS_BOTTOM_OFFSET - y}px`,
    left: windowWidth / 2 - CALL_CONTROLS_WIDTH_PX / 2 + x,
  });

  if (!callState) {
    return;
  }

  return isInCall ? (
    <Draggable
      id={DraggableItemIds.CALL_CONTROLS}
      calculateAbsolutePosition={handleCalculateAbsolutePosition}
    >
      <CallControls
        show={show}
        established={established}
        formattedTime={formattedTime}
        hasSession={hasSession}
        held={held}
        inTransfer={inTransfer}
        isAudioMuted={isAudioMuted}
        isVideoMuted={isVideoMuted}
        onHangup={handleHangup}
        onAnswer={handleAnswer}
        onHold={() => SoftPhoneModule.callControl.hold()}
        onMute={() => SoftPhoneModule.callControl.mute()}
        onStartTimer={start}
        onUnhold={() => SoftPhoneModule.callControl.unhold(callId)}
        onUnmute={() => SoftPhoneModule.callControl.unmute()}
        progress={progress}
        identity={identity}
        terminating={terminating}
        isRecording={isRecording}
        isRecordingDisabled={isRecordingDisabled}
        isRecordingLoading={isRecordingLoading}
        recordingTooltip={recordingTooltip}
        toggleRecording={() => toggleRecording(remoteCallId)}
      />
    </Draggable>
  ) : null;
}
